import { useEffect, useRef } from "react";

/**
 * Adds `data-revealed="true"` to the element when it crosses the viewport.
 * One-shot: once revealed, the observer is disconnected. Pair with the
 * CSS in `index.css` that fades / translates `[data-revealed]` elements.
 *
 * Used by showcase sections — kept narrow on purpose, no library, no state.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(
  options?: IntersectionObserverInit
) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      // Either not mounted or running in a non-DOM env (vitest jsdom is fine,
      // but defensive against SSR + ancient browsers). Just reveal immediately.
      if (el) el.setAttribute("data-revealed", "true");
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            (e.target as HTMLElement).setAttribute("data-revealed", "true");
            obs.unobserve(e.target);
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05, ...options }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return ref;
}
